/**
 * Mounts the loyalty blocks.
 *
 * Every block renders one element carrying `data-loyalty-block` (the slug) and
 * `data-loyalty-props` (a JSON envelope: block, instanceId, editor, and the editor's copy under
 * `labels`). App\Loyalty\LoyaltyBlock builds that payload; this file is the other end of it.
 *
 * Preact via preact/compat — the library ships React source and names this as its WordPress path.
 * The aliases live in vite.config.ts.
 */

import { render } from 'preact';
import { LoyaltyProvider } from '@drewling/urc-loyalty-ui';
import { getClient, resetClient } from './loyalty/client';
import { authState, idToken, onIdentityChange } from './loyalty/urcAuth';
import { configureLoyaltyConsent } from './loyalty/consent';
import { JoinBlock } from './loyalty/blocks/JoinBlock';
import { JoinStripBlock } from './loyalty/blocks/JoinStripBlock';
import { ProfileHeaderBlock } from './loyalty/blocks/ProfileHeaderBlock';
import { ProfileBlock } from './loyalty/blocks/ProfileBlock';
import { initLoyaltyToasts } from './loyalty/toasts';
import { resetToastGate } from './loyalty/toasts/gate';
import { forgetToastBaseline } from './loyalty/toasts/identity';
import { clearToasts } from './loyalty/toasts/queue';
import { initLoyaltyNavLink } from './loyalty/navLink';
import { initNonMemberRedirect } from './loyalty/nonMemberRedirect';
import { takeLeaveOutcome } from './loyalty/leaveLoyalty';
import { resumeMembership } from './loyalty/membership';

/** Marks an element as mounted, so a second pass over the DOM is a no-op. */
const MOUNTED = 'loyaltyMounted';

/**
 * Slug to component. Looked up by EQUALITY and never by prefix: 'loyalty-profile' is a strict
 * prefix of 'loyalty-profile-header', so a startsWith would mount the dashboard into the hero.
 */
const BLOCKS = {
	'loyalty-join': JoinBlock,
	// Not an ACF block: the home page's join strip is a Blade partial that borrows this mount,
	// because resources/views/page.blade.php never calls the_content() on the front page. Note
	// that 'loyalty-join' is a strict prefix of this key too -- the equality rule above is what
	// keeps the enrolment funnel out of the strip.
	'loyalty-join-strip': JoinStripBlock,
	'loyalty-profile-header': ProfileHeaderBlock,
	'loyalty-profile': ProfileBlock,
};

/**
 * @param {HTMLElement} element
 * @return {Object|null} The parsed props envelope, or null when unusable.
 */
function readProps( element ) {
	const raw = element.dataset.loyaltyProps;

	if ( ! raw ) {
		return null;
	}

	try {
		return JSON.parse( raw );
	} catch ( error ) {
		console.warn( '[loyalty] unreadable block props', error );

		return null;
	}
}

function mount( element ) {
	if ( element.dataset[ MOUNTED ] === '1' ) {
		return;
	}

	const props = readProps( element );

	if ( ! props ) {
		return;
	}

	// ACF replaces a block's entire markup on every field change through its fetch-block AJAX.
	// Nothing is mounted there on purpose: a half-live app thrown away on each keystroke is worse
	// than the placeholder.
	//
	// This used to say the markup sits "inside an editor iframe this bundle is not loaded into",
	// which would make this bail dead code. Doubtful: the loyalty blocks register at ACF
	// `api_version` 2 and dozens of registered types are below apiVersion 3, so the canvas is not
	// the iframed one. Treat the bail as load-bearing until somebody checks wp-admin --
	// ./loyalty/nonMemberRedirect.js now makes the same check for a much higher price, since it
	// NAVIGATES. See docs/subsystems/gutenberg-blocks.md.
	if ( props.editor ) {
		return;
	}

	const Block = BLOCKS[ props.block ];

	if ( ! Block ) {
		return;
	}

	element.dataset[ MOUNTED ] = '1';
	element.textContent = '';

	// Everything except the three routing keys is forwarded as-is. The server omits a blank field
	// rather than sending '' (D-039), so an absent key here means "the editor authored nothing"
	// and each block's own parameter default is what fills it in — which is why nothing is
	// normalised on the way through. `block`, `instanceId` and `editor` steer this function and
	// are not props of anything.
	const { block, instanceId, editor, ...componentProps } = props;
	const entry = { element, Block, props: componentProps };

	painted.push( entry );
	paint( entry );
}

/**
 * Render one block into its own container.
 *
 * Kept apart from mount() because it is run a second time: see watchForIdentityChange(). Everything
 * that happens once -- the props envelope, the editor bail, emptying the container, the mounted
 * flag -- stays in mount(), and this does nothing but hand Preact the current client. A repaint
 * must NOT clear the container: the previous tree is what Preact diffs against.
 *
 * @param {{element: HTMLElement, Block: Function, props: Object}} entry What mount() recorded.
 * @return {void}
 */
function paint( { element, Block, props } ) {
	render(
		<LoyaltyProvider client={ getClient() }>
			<Block { ...props } />
		</LoyaltyProvider>,
		element
	);
}

/** Every block this page mounted, in document order, so they can be repainted together. */
const painted = [];

/**
 * Everything in this bundle that belongs to ONE fan, dropped in one place.
 *
 * **The rule this obeys, and it is the rule rather than this list:** every per-fan store carries
 * the fan's `sub` in its key or its value, so an account switch is a **miss by construction**.
 * `urc.loyalty.consent`, `urc.loyalty.toastEnrolled` and `urc.loyalty.toastFeedMark` are all that
 * shape, and none of them appears below — they need no clearing. What is left here is the state
 * that CANNOT be fan-keyed, either because a third party owns the key or because it is in memory:
 *
 * - **The API client.** Its prefetch cache is keyed by method name with no fan in it, and its
 *   in-flight coalescing is keyed by a host-wide scope plus the URL (D-087). A new client drops
 *   both, and is the only way out of a `requiresReauth` error. It is no longer the only way to
 *   make `useLoyaltyData` read again -- uxlib 0.14.0 added `revalidation.refresh()` -- but a
 *   refresh keeps the caches, which is the opposite of what this function is for (D-079).
 * - **The toast snapshot**, `urc.loyalty.toastSnapshot`, which is the library's key. Cleared
 *   together with this fan's feed mark, because a mark left beside a cleared snapshot lets the tab
 *   skip the reconcile that was supposed to re-baseline and swallows the fan's next award.
 * - **The gate chain's verdict**, held in module memory for the life of the page and shared with
 *   the header's loyalty links and the profile blocks' non-member redirect. An enrolled fan
 *   switching to a not-enrolled one would otherwise not be bounced off a members-only page.
 * - **The toasts on screen.** A congratulation addressed to the previous fan.
 *
 * **`clearTiersCache()` is deliberately NOT here**, and the plan for this work had it on the list.
 * `getTiers` is `auth: 'public'` in `@drewling/urc-loyalty-api` — no `Authorization` header at all,
 * which is why `consentGate.js` exempts it — so the ladder is identical for every fan and there is
 * nothing per-fan in it to drop. `client.js` clears it once per page load for a different reason:
 * the ENVIRONMENT behind the client changing across a deploy.
 *
 * **The score tracker is not here either, and cannot be.** It lives in the `app` bundle and D-055
 * forbids importing across that line, so `resources/scripts/tracking/index.js` subscribes to
 * `window.urcAuth.onIdentityChange` itself. Its reset is also a different shape — the SDK assigns
 * its visitor id in the constructor, so it needs a new instance rather than a restart.
 *
 * **Called before anything repaints.** The store announces its identity listeners before its change
 * listeners, deliberately, so a subscriber that repaints cannot read a cache this is about to drop.
 *
 * @return {void}
 */
function resetLoyaltyState() {
	resetClient();
	forgetToastBaseline();
	resetToastGate();
	clearToasts();
}

/** Whether the block-independent subscription has been made. */
let watchingWithoutBlocks = false;

/**
 * The same reset, on a page carrying no loyalty block.
 *
 * `watchForIdentityChange()` is armed only when something mounted, because its job is repainting
 * what mounted. Two of the four things `resetLoyaltyState()` drops belong to the fan rather than to
 * a block — the toast layer is site-wide, and the gate memo is shared with the header's loyalty
 * links — so a switch on an ordinary page still has to be handled.
 *
 * @return {void}
 */
function watchForIdentityChangeWithoutBlocks() {
	if ( watching || watchingWithoutBlocks ) {
		return;
	}

	watchingWithoutBlocks = true;

	onIdentityChange( resetLoyaltyState );
}

/*
 * Point the loyalty-consent module at this site's credential and identity.
 *
 * **Position.** Strictly before every consumer: mountAll(), initLoyaltyToasts(),
 * initLoyaltyNavLink() and initNonMemberRedirect() are all invoked below this line, and
 * getClient() -- which is where the gate lives -- is not called until one of them runs. Nothing in
 * loyalty/consent does anything at import, so calling this is what brings it to life.
 *
 * **`getCredential` is async now**, and `getIdentity` is separate from it. They used to be one
 * synchronous cookie read, with the fan's `sub` decoded out of the bearer. The `sub` comes straight
 * off the AuthState, which is derived from a token the SDK has validated -- so a cache entry can no
 * longer be attached to a fan whose token nobody checked, and the consent read no longer has to
 * wait for a bearer in order to know WHO it is reading for.
 *
 * **And the credential is the ID TOKEN, not the access token.** That is
 * [D-068](../../docs/decisions.md) closed: an ID token's `aud` is this site's own Auth0 client id,
 * so it is addressed to us, while an access token is addressed to the 3Halves loyalty API -- a real
 * third party that holds every fan's token in the normal course of business. Presenting the latter
 * to a WordPress endpoint is audience confusion, and it was accepted only as a bridge until the ID
 * token could be obtained at all. `window.urcAuth.getIdToken()` is that bridge spent.
 */
configureLoyaltyConsent( {
	getCredential: idToken,
	getIdentity: () => {
		const state = authState();

		return state && state.sub ? state.sub : '';
	},
} );

/** Whether the subscription below has already been made. */
let watching = false;

/**
 * Hand every mounted block a new client whenever the fan changes.
 *
 * **What this replaces, and why the cue is different.** It used to be armed on "a credential turned
 * up on a page that started without one" -- the D-066 gap, where a third-party script expired every
 * `auth0_*` cookie on `DOMContentLoaded` and the silent probe re-issued the token a second or two
 * later, so a navigation landing between the two was served to a browser carrying no credential at
 * all. Every self-scoped read then raised `LoyaltyAuthError` before the wire and the library
 * rendered "Session expired" with no Retry control, because `requiresReauth` answers `retry: null`.
 * Nothing changed the client identity, so the blocks stayed dead until a reload. **That gap is
 * closed at the source: there is no cookie for anything to delete, and the SDK answers a token
 * request by minting one.**
 *
 * The cue that remains is the one nothing ever covered: **one fan replacing another.** A new client
 * makes the tree read again -- `useLoyaltyData` keys its effect on the client object -- and it
 * drops the prefetch cache and the in-flight coalescing with it, which is what stops fan B being
 * served fan A's challenges, badges, leaderboard and profile stats out of a 30s method-keyed memo.
 * uxlib 0.14.0's `revalidation.refresh()` re-reads a slice without a new client, so a rebuild is
 * no longer the ONLY way to refetch -- but it keeps both caches, so it is no use here.
 *
 * **It does not fire on an ordinary load.** `onIdentityChange` compares the fan against the owner
 * recorded in this tab's `sessionStorage`, so a fan reopening their own tab is not a change. That
 * property is what makes hanging a reset off it affordable at all -- see
 * `resources/scripts/auth/store.js`.
 *
 * **Not armed on a page with no block.** The three other riders on this bundle -- loyalty/toasts,
 * loyalty/navLink.js and loyalty/nonMemberRedirect.js -- take their answer from
 * loyalty/toasts/gate.js and need nothing from here.
 *
 * @return {void}
 */
function watchForIdentityChange() {
	if ( watching || painted.length === 0 ) {
		return;
	}

	watching = true;

	onIdentityChange( () => {
		resetLoyaltyState();

		painted.forEach( entry => {
			// Isolated per block: a single block that throws during its repaint must not leave the
			// rest of the page in its error state.
			try {
				paint( entry );
			} catch ( error ) {
				console.warn( '[loyalty] could not repaint block', error );
			}
		} );
	} );
}

function mountAll() {
	document.querySelectorAll( '[data-loyalty-block]' ).forEach( mount );
	watchForIdentityChange();

	// Armed even on a page with no block, because two of the three things below belong to the fan
	// rather than to a block: the toast layer is site-wide, and the gate memo is shared with the
	// header's loyalty links.
	watchForIdentityChangeWithoutBlocks();
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', mountAll );
} else {
	mountAll();
}

// The toast layer belongs to the fan rather than to a page, so it is not part of the block sweep
// above: it builds its own root and mounts nothing unless the fan turns out to be eligible. Called
// at module top level and outside the readyState branch on purpose -- this entry is a deferred
// module, so document.body already exists, and nothing here depends on a block being present.
// See loyalty/toasts/index.js.
initLoyaltyToasts();

// Same reasoning, and it rides the same gate chain: the site header is on every page, not in a
// block, and the link it repoints is server-rendered for everyone. See loyalty/navLink.js.
initLoyaltyNavLink();

// Third rider on that same chain, and the only one that can navigate. It reads a data attribute on
// the block rather than the props above, because it has to work whether or not anything mounted --
// including for a visitor whose verdict is "not a member", where the mounted blocks would only
// render errors. Costs one querySelector on a page with no target set. See
// loyalty/nonMemberRedirect.js.
initNonMemberRedirect();

// Republish what a leave attempt came to, on the page the fan was sent to afterwards.
//
// The attribute could not be written where the attempt happened: that flow ends in
// location.replace(), so anything on <html> -- and the console with it -- is destroyed a tick later.
// The one branch worth reading is `incomplete`, which means the fan HAS left here and the request
// did not reach 3Halves, and it is also the branch that is guaranteed on every deployed origin but
// dev. Read once and dropped, so a later page of the same browse does not keep answering for a
// navigation that is over. See loyalty/leaveLoyalty.js.
publishLeaveOutcome();

// Finish a join or leave an earlier page left half done: its 3Halves leg, paced by the server, or a
// rejoin's one extra look for a late withdrawal raise. Reads one localStorage key and sends
// nothing when nothing is due, which is every page for every fan who has not just joined or left.
// See loyalty/membership/index.js and docs/subsystems/loyalty-withdrawal.md.
resumeMembership();

/**
 * @return {void}
 */
function publishLeaveOutcome() {
	const state = authState();
	const outcome = takeLeaveOutcome( state && state.sub ? state.sub : '' );

	if ( outcome && document.documentElement ) {
		document.documentElement.setAttribute( 'data-loyalty-withdrawal', outcome );
	}
}
